// ======================================================================
// File: src/modules/ppe/transport/components/detail/TransportDetail.jsx
// ======================================================================

import React, { useState } from "react";

import transportService from "../../services/transportService";
import VehicleDrawer from "../vehicle/VehicleDrawer";
import PaymentDrawer from "../payment/PaymentDrawer";
import TransportStatusBadge from "../../shared/TransportStatusBadge";
import TransportBookingCard from "./TransportBookingCard";

export default function TransportDetail({ transport, reload }) {

  const [showVehicleDrawer, setShowVehicleDrawer] = useState(false);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [processingVehicleId, setProcessingVehicleId] = useState(null);

  // ------------------------------------------------------------------
  // CLOSE TRANSPORT
  // ------------------------------------------------------------------
  const handleCloseTransport = async () => {
    try {
      await transportService.closeTransport(transport.id);
      reload();
    } catch (err) {
      console.error(err);
      alert("Unable to close transport");
    }
  };

  // ------------------------------------------------------------------
  // MARK PICKUP
  // ------------------------------------------------------------------
  const handlePickup = async (vehicleId) => {
    try {
      setProcessingVehicleId(vehicleId);
      await transportService.markVehiclePickup(vehicleId);
      reload();
    } catch (err) {
      console.error(err);
      alert("Failed to mark pickup");
    } finally {
      setProcessingVehicleId(null);
    }
  };

  // ------------------------------------------------------------------
  // MARK DELIVERY
  // ------------------------------------------------------------------
  const handleDelivery = async (vehicleId) => {
    try {
      setProcessingVehicleId(vehicleId);
      await transportService.markVehicleDelivery(vehicleId);
      reload();
    } catch (err) {
      console.error(err);
      alert("Failed to mark delivery");
    } finally {
      setProcessingVehicleId(null);
    }
  };

  return (
    <div className="transport-detail">

      {/* ============================================================
          HEADER CARD
      ============================================================ */}
      <div className="card glass-card mb-3">
        <div className="card-body">

          <div className="d-flex justify-content-between">
            <div>
              <h5 className="mb-1">{transport.transport_code}</h5>

              <div className="text-muted">
                {transport.source_name} → {transport.destination_name}
              </div>

              {transport.transporter_name_snapshot && (
                <div className="mt-1 text-primary fw-semibold">
                  🚚 {transport.transporter_name_snapshot}
                </div>
              )}

              <div className="mt-2">
                <TransportStatusBadge status={transport.status_code} />

                <span className="badge bg-secondary ms-2">
                  Payment: {transport.overall_payment_status}
                </span>
              </div>
            </div>

            <div>
              <button
                className="btn btn-outline-primary me-2"
                onClick={() => setShowVehicleDrawer(true)}
              >
                + Add Vehicle
              </button>

              <button
                className="btn btn-outline-success me-2"
                onClick={() => setShowPaymentDrawer(true)}
              >
                + Add Payment
              </button>

              <button
                className="btn btn-danger"
                onClick={handleCloseTransport}
              >
                Close Transport
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ============================================================
          BOOKING CARD
      ============================================================ */}
      <TransportBookingCard
        transport={transport}
        reload={reload}
      />

      {/* ============================================================
          VEHICLES
      ============================================================ */}
      <div className="card glass-card mb-3">
        <div className="card-header">
          <strong>Vehicles</strong>
        </div>

        <div className="card-body p-0">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Driver</th>
                <th>Freight</th>
                <th>Status</th>
                <th>Pickup</th>
                <th>Delivery</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {transport.vehicles?.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center p-3">
                    No vehicles added
                  </td>
                </tr>
              )}

              {transport.vehicles?.map((v) => (
                <tr key={v.id}>
                  <td>{v.vehicle_no}</td>
                  <td>{v.driver_name}</td>
                  <td>{v.freight_amount}</td>

                  <td>
                    <span className="badge bg-info">
                      {v.status_code}
                    </span>
                  </td>

                  <td>{v.actual_pickup_datetime || "-"}</td>
                  <td>{v.actual_delivery_datetime || "-"}</td>

                  {/* ===================================================
                      STRICT STATUSMASTER ACTIONS
                  =================================================== */}
                  <td>
                    {v.status_code === "ASSIGNED" && (
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={processingVehicleId === v.id}
                        onClick={() => handlePickup(v.id)}
                      >
                        🚚 Mark Pickup
                      </button>
                    )}

                    {v.status_code === "PICKED_UP" && (
                      <button
                        className="btn btn-sm btn-success"
                        disabled={processingVehicleId === v.id}
                        onClick={() => handleDelivery(v.id)}
                      >
                        📦 Mark Delivery
                      </button>
                    )}

                    {v.status_code === "DELIVERED" && (
                      <span className="badge bg-success">
                        ✅ Completed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============================================================
          PAYMENT SUMMARY
      ============================================================ */}
      <div className="card glass-card">
        <div className="card-header">
          <strong>Financial Summary</strong>
        </div>

        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <label>Total Freight</label>
              <h6>{transport.total_freight_amount}</h6>
            </div>

            <div className="col-md-3">
              <label>Total Tax</label>
              <h6>{transport.total_tax_amount}</h6>
            </div>

            <div className="col-md-3">
              <label>Advance Paid</label>
              <h6>{transport.total_advance_amount}</h6>
            </div>

            <div className="col-md-3">
              <label>Payable</label>
              <h6>{transport.total_payable_amount}</h6>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          DRAWERS
      ============================================================ */}
      {showVehicleDrawer && (
        <VehicleDrawer
          transportId={transport.id}
          onClose={() => setShowVehicleDrawer(false)}
          onSaved={() => {
            setShowVehicleDrawer(false);
            reload();
          }}
        />
      )}

      {showPaymentDrawer && (
        <PaymentDrawer
          transportId={transport.id}
          vehicles={transport.vehicles}
          onClose={() => setShowPaymentDrawer(false)}
          onSaved={() => {
            setShowPaymentDrawer(false);
            reload();
          }}
        />
      )}

    </div>
  );
}
